import React from 'react'
import { motion } from 'motion/react'

interface ChatListRowProps {
    title: React.ReactNode
    // One line of detail under the title. Rendered inside a single
    // truncating line, so pass inline content only (text and spans).
    meta: React.ReactNode
    onContinue: () => void
    onDelete: (event: React.MouseEvent) => void
}

// One row of a chat history list — shared by `/advice` and `/genie/history`.
// From `sm` up it is title-and-meta on the left, actions on the right. Below
// `sm` the actions move to their own right-aligned line under the title, so
// neither "Continue Chat →" nor the meta line has to share a phone's width
// with the other and break into narrow stacks.
const ChatListRow: React.FC<ChatListRowProps> = ({ title, meta, onContinue, onDelete }) => (
    <div className="bg-white p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0 sm:flex-1">
                <h4 className="font-medium text-gray-900">{title}</h4>
                <p className="mt-1 text-sm text-gray-500 truncate">{meta}</p>
            </div>
            <div className="flex items-center justify-end gap-2 sm:shrink-0">
                <button
                    onClick={onContinue}
                    className="text-amber-800 hover:text-amber-900 text-sm cursor-pointer whitespace-nowrap"
                >
                    Continue Chat →
                </button>
                <motion.button
                    onClick={onDelete}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    className="text-gray-500 hover:text-rose-600 cursor-pointer"
                    title="Delete chat"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                    </svg>
                </motion.button>
            </div>
        </div>
    </div>
)

export default ChatListRow
